import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Modal, Slider, Upload } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import Cropper from "react-easy-crop";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { BASE_URL } from "../../Constants";

// Super-admin only: platform-wide default logos, stored per key in the lookups container.
// "marriage" is applied to new social gatherings; "virtual-portal" is the header logo for
// virtual (individual) companies that have no logo of their own.
export const WEDDING_EVENT_TYPE = "marriage";
export const VIRTUAL_PORTAL_LOGO_KEY = "virtual-portal";

// Logos are cropped to a square before upload (see the 1:1 hint). Zooming out below 1 pads the
// image with transparency so a wide logo can fit without being cut off.
const LOGO_OUTPUT_SIZE = 512;

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });

const cropToLogoFile = async (src, area) => {
  const img = await loadImage(src);
  const canvas = document.createElement("canvas");
  canvas.width = LOGO_OUTPUT_SIZE;
  canvas.height = LOGO_OUTPUT_SIZE;
  const scale = LOGO_OUTPUT_SIZE / area.width;
  // Draw with explicit offsets (instead of a source rect) so areas outside the image stay transparent.
  canvas
    .getContext("2d")
    .drawImage(img, -area.x * scale, -area.y * scale, img.width * scale, img.height * scale);
  const blob = await new Promise((resolve) => canvas.toBlob(resolve, "image/png"));
  return new File([blob], "logo.png", { type: "image/png" });
};

function DefaultLogoBlock({ logoKey, titleKey, descriptionKey, savedKey, removedKey }) {
  const { t } = useTranslation();
  const [logoUrl, setLogoUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const [source, setSource] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState(null);

  useEffect(() => {
    axios
      .get(`${BASE_URL}/Lookup/default-event-logo/${logoKey}`)
      .then((res) => setLogoUrl(res.data?.logoUrl || ""))
      .catch(() => toast.error(t("settings.defaultLogos.loadFailed")));
  }, [logoKey, t]);

  const saveLogo = async (url, successKey) => {
    await axios.put(`${BASE_URL}/Lookup/default-event-logo/${logoKey}`, {
      logoUrl: url,
    });
    setLogoUrl(url);
    toast.success(t(successKey));
  };

  const pickFile = (info) => {
    const file = info.file.originFileObj || info.file;
    if (file.type !== "image/png" && file.type !== "image/jpeg") {
      toast.error(t("settings.defaultLogos.invalidType"));
      return;
    }
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setSource(URL.createObjectURL(file));
  };

  const closeCropper = () => {
    if (source) URL.revokeObjectURL(source);
    setSource(null);
  };

  const applyCrop = async () => {
    if (!area) return;
    const formData = new FormData();
    try {
      setSaving(true);
      formData.append("file", await cropToLogoFile(source, area));
      const response = await axios.post(`${BASE_URL}/Upload/image`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await saveLogo(response.data.url, savedKey);
      closeCropper();
    } catch (error) {
      toast.error(t("settings.defaultLogos.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async () => {
    try {
      setSaving(true);
      await saveLogo("", removedKey);
    } catch (error) {
      toast.error(t("settings.defaultLogos.saveFailed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="py-6 max-w-xl">
      <h2 className="text-lg font-semibold text-gray-900">{t(titleKey)}</h2>
      <p className="text-sm text-gray-500 mb-1">{t(descriptionKey)}</p>
      <p className="text-xs text-gray-400 mb-4">{t("settings.defaultLogos.hint")}</p>

      {logoUrl ? (
        <img
          src={logoUrl}
          alt={t(titleKey)}
          className="w-32 h-32 object-contain rounded bg-gray-50 border border-gray-200 mb-4"
        />
      ) : (
        <p className="text-sm text-gray-400 mb-4">{t("settings.defaultLogos.none")}</p>
      )}

      <div className="flex gap-2">
        <Upload
          accept="image/png,image/jpeg"
          showUploadList={false}
          beforeUpload={() => false}
          onChange={pickFile}
        >
          <Button icon={<UploadOutlined />} loading={saving}>
            {logoUrl ? t("settings.defaultLogos.change") : t("settings.defaultLogos.upload")}
          </Button>
        </Upload>
        {logoUrl && (
          <Button danger onClick={handleRemove} disabled={saving}>
            {t("settings.defaultLogos.remove")}
          </Button>
        )}
      </div>

      <Modal
        open={!!source}
        title={t("settings.defaultLogos.adjustTitle")}
        onCancel={closeCropper}
        onOk={applyCrop}
        okText={t("settings.defaultLogos.apply")}
        confirmLoading={saving}
        width={480}
        destroyOnClose
      >
        <p className="text-xs text-gray-500 mb-2">{t("settings.defaultLogos.adjustHint")}</p>
        <div style={{ position: "relative", height: 320, background: "#222" }}>
          {source && (
            <Cropper
              image={source}
              crop={crop}
              zoom={zoom}
              minZoom={0.5}
              maxZoom={3}
              aspect={1}
              restrictPosition={false}
              objectFit="contain"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setArea(pixels)}
            />
          )}
        </div>
        <Slider min={0.5} max={3} step={0.01} value={zoom} onChange={setZoom} style={{ marginTop: 12 }} />
      </Modal>
    </div>
  );
}

function DefaultLogosSettings() {
  return (
    <>
      <DefaultLogoBlock
        logoKey={WEDDING_EVENT_TYPE}
        titleKey="settings.defaultLogos.weddingTitle"
        descriptionKey="settings.defaultLogos.weddingDescription"
        savedKey="settings.defaultLogos.saved"
        removedKey="settings.defaultLogos.removed"
      />
      <DefaultLogoBlock
        logoKey={VIRTUAL_PORTAL_LOGO_KEY}
        titleKey="settings.defaultLogos.portalTitle"
        descriptionKey="settings.defaultLogos.portalDescription"
        savedKey="settings.defaultLogos.portalSaved"
        removedKey="settings.defaultLogos.portalRemoved"
      />
    </>
  );
}

export default DefaultLogosSettings;
